import type { TokenClaims, Workspace } from "@/definitions/types";
import type { App } from "vue";
import type { Router } from "vue-router";
import * as Sentry from "@sentry/vue";
import {
  API_HOST,
  ENVIRONMENT,
  PRODUCT_VERSION,
  SENTRY_DSN,
} from "@/constants/envs";

/**
 * Sentry 초기화
 * - local 은 `enabled: false` 라서 이벤트를 보내지 않는다. DSN 이 비어 있으면 초기화 자체를 건너뛴다(30차 P30-05 — `setupSentry` 첫 줄).
 * - 트랜잭션 이름을 `/org-fit/mission` 처럼 라우트 기준으로 남기려고 router 를 넘긴다.
 * - `tracePropagationTargets` 에 API 호스트를 넣어 백엔드(FastAPI) 트레이스와 이어 붙인다.
 *   백엔드 CORS 가 `allow_headers=["*"]` 이므로 `sentry-trace` / `baggage` 헤더가 막히지 않는다.
 * - 채용 도메인이라 개인정보가 섞이기 쉬우므로 기본 PII 수집을 끈다. SDK 11 에서 `sendDefaultPii` 가 없어지고
 *   `dataCollection` 으로 바뀌었는데, **생략하면 모두 수집이 기본값**이다(`@sentry/core` 11
 *   `resolveDataCollectionOptions` 의 `DEFAULTS`). 그래서 10.x 의 `sendDefaultPii: false` 매핑
 *   (10.75.2 `defaultPiiToCollectionOptions`)과 같은 보호 수준을 명시한다.
 * - 10.75.2 는 헤더 · 쿠키 · 쿼리에 `PII_HEADER_SNIPPETS` 거부 목록을 걸었는데 11 은 그 상수를 없앴다. 11 이
 *   늘 거르는 것은 민감 키(`SENSITIVE_KEY_SNIPPETS` — auth · token · cookie 등)뿐이라 IP · 경유지 헤더
 *   (`X-Forwarded-For` · `X-Real-IP` · `Via` 등)는 기본값이면 실린다. 그래서 10.75.2 의 값을 그대로 옮겨 둔다.
 */
/** 10.75.2 `filtering-snippets.js` 의 `PII_HEADER_SNIPPETS` 원문(11 에서 삭제됨). 키에 이 조각이 들어가면 거른다. */
const PII_KEY_SNIPPETS = ["forwarded", "-ip", "remote-", "via", "-user"];

const TRACES_SAMPLE_RATES: Record<string, number> = {
  sandbox: 1,
  prod: 0.2,
};

/**
 * 경로에 원문이 실리는 비밀값. 담당자 입력 링크(`/job-input/{token}`) · 입사서류 제출 링크
 * (`/onboarding-upload/{token}`) · 채용 페이지 지원 링크(`/apply/{slug}`) 세 가지다.
 *
 * 이 토큰을 가진 사람은 로그인 없이 그 직무의 입력 화면을 열고 제출하거나, 그 합격자의 입사서류
 * 제출 화면을 열고 파일을 올릴 수 있다. Sentry 이벤트의 `request.url`·트랜잭션 이름·네비게이션
 * breadcrumb 에는 브라우저가 보고 있는 **실제 경로**가 들어가므로, 가리지 않으면 이슈를 볼 수 있는
 * 사람이 남의 링크를 그대로 쓸 수 있다(채용 페이지 slug 는 공개 식별자지만 마감 · 지원 통계가 로그에 엮이지 않게 같이 가린다). 서버 호출은 토큰을 본문으로 보내 URL 에 남기지 않지만
 * (`views/job-fit/jobInput.api.ts`, `views/operations/onboardingUpload.api.ts`, `views/operations/publicApply.api.ts`) 화면 경로에는 남을
 * 수밖에 없어, 여기가 마지막 차단 지점이다.
 *
 * 새 공개 링크 화면이 생기면 이 정규식에 경로를 추가한다.
 */
const SECRET_PATH_SEGMENT =
  /(\/(?:job-input|onboarding-upload|apply)\/)[^/?#]+/g;

function maskSecretPaths(value: string): string {
  return value.replace(SECRET_PATH_SEGMENT, "$1***");
}

/** 문자열일 때만 가린 값을, 아니면 undefined 를 돌려준다(원래 값을 건드리지 않기 위해). */
function maskIfString(value: unknown): string | undefined {
  return typeof value === "string" ? maskSecretPaths(value) : undefined;
}

/**
 * 이벤트에 실린 경로를 모두 가린다.
 *
 * 오류와 트랜잭션이 같은 자리(`transaction`·`request.url`·헤더)를 쓰므로 양쪽에 건다. **단, SDK 11 은 기본이 스팬 스트리밍(`traceLifecycle: "stream"`)이라
 * `beforeSendTransaction` 은 아예 불리지 않는다**(30차 P30-01) — 스팬은 아래 `scrubSpan`(`beforeSendSpan`)이 같은 마스킹을 건다. 트랜잭션 훅은
 * `traceLifecycle: "static"` 으로 돌릴 때를 위한 보험이다 — Sentry 의
 * vue 라우터 계측은 라우트에 `name` 이 없으면 템플릿(`/job-input/:token` · `/onboarding-upload/:token`)이 아니라 **실제 경로**를
 * 트랜잭션 이름으로 쓴다.
 */
interface ScrubbableEvent {
  transaction?: string;
  request?: { url?: string; headers?: Record<string, string> };
}

function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  const masked = maskIfString(event.transaction);
  if (masked !== undefined) {
    event.transaction = masked;
  }
  const request = event.request;
  if (request) {
    const url = maskIfString(request.url);
    if (url !== undefined) {
      request.url = url;
    }
    const headers = request.headers;
    if (headers) {
      // 헤더 이름의 대소문자는 보내는 쪽마다 다르다(`Referer` / `referer`)라 키를 훑는다.
      for (const key of Object.keys(headers)) {
        const value = maskIfString(headers[key]);
        if (value !== undefined) {
          headers[key] = value;
        }
      }
    }
  }
  return event;
}

/** 키에 이 조각이 있으면 값을 가린다(Authorization 헤더 · 토큰 · 쿠키 · 비밀번호) */
const SENSITIVE_KEY = /authorization|token|cookie|password|secret|credential/i;

/**
 * 콘솔 breadcrumb 의 인자에서 비밀값을 걷어 낸다(27차 보안 확인).
 *
 * `logger.error("…", error)` 가 부르는 `console.error` 는 Sentry 콘솔 breadcrumb 에 **원본 인자**로 남는데, axios 오류는 `config.headers.Authorization`
 * (액세스 · 리프레시 토큰)을 소유 속성으로 들고 있다. 서버 쪽 데이터 스크러빙에만 기대지 않고 여기서 먼저 막는다. `Error` 는 이름 · 메시지 · 상태 ·
 * 메서드 · 경로(비밀 경로는 가림)만 남기고, 객체는 민감 키를 가리며 깊이를 제한한다.
 */
function sanitizeLogArg(arg: unknown, depth = 0): unknown {
  if (arg instanceof Error) {
    const axiosLike = arg as {
      code?: unknown;
      response?: { status?: unknown };
      config?: { method?: unknown; url?: unknown };
    };
    return {
      name: arg.name,
      message: arg.message,
      code: axiosLike.code,
      status: axiosLike.response?.status,
      method: axiosLike.config?.method,
      url: maskIfString(axiosLike.config?.url),
    };
  }
  if (typeof arg !== "object" || arg === null) return arg;
  if (depth >= 3) return "[Object]";
  if (Array.isArray(arg)) {
    return arg.map((item) => sanitizeLogArg(item, depth + 1));
  }
  return Object.fromEntries(
    Object.entries(arg).map(([key, value]) => [
      key,
      SENSITIVE_KEY.test(key) ? "[Filtered]" : sanitizeLogArg(value, depth + 1),
    ]),
  );
}

/**
 * 스팬(스트리밍) 하나에서 비밀 경로를 가린다(30차 P30-01). 공개 링크 화면의 페이지로드 · 내비게이션 스팬은 `browser.request` · `browser.response` ·
 * `browser.dns` 같은 성능 스팬을 달고, 이 스팬들의 `url.full` · `http.url` · `url.path` 등 속성에 **실제 주소(토큰 원문)** 가 실린다. 이름(`name`)과 모든
 * 문자열 속성값(원시값 · `{ value }` 객체 · 문자열 배열)을 훑어 같은 정규식으로 가린다 — 속성 이름을 나열하면 SDK 가 새 속성을 달 때 다시 샌다.
 */
/** 스트리밍 스팬 한 개(`@sentry/core` 의 `StreamedSpanJSON` — `@sentry/vue` 가 다시 내보내지 않아 옵션 타입에서 꺼낸다) */
type StreamedSpan = Parameters<
  NonNullable<NonNullable<Parameters<typeof Sentry.init>[0]>["beforeSendSpan"]>
>[0];

function maskAttributeValue(value: unknown): unknown {
  if (typeof value === "string") return maskSecretPaths(value);
  if (Array.isArray(value)) {
    return value.map((item) =>
      typeof item === "string" ? maskSecretPaths(item) : item,
    );
  }
  if (typeof value === "object" && value !== null && "value" in value) {
    const wrapped = value;
    return { ...wrapped, value: maskAttributeValue(wrapped.value) };
  }
  return value;
}

function scrubSpan(span: StreamedSpan): StreamedSpan {
  span.name = maskSecretPaths(span.name);
  const attributes = span.attributes as Record<string, unknown>;
  for (const key of Object.keys(attributes)) {
    attributes[key] = maskAttributeValue(attributes[key]);
  }
  return span;
}

export function setupSentry(app: App, router: Router): void {
  // DSN 이 비면 초기화하지 않는다(30차 P30-05) — 빈 DSN 으로 `init` 하면 SDK 는 비활성이지만 환경 검증이 먼저 앱을 멈추던 것을 막는다.
  if (!SENTRY_DSN) return;
  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    enabled: ENVIRONMENT !== "local",
    environment: ENVIRONMENT,
    release: `selechub-frontend@${PRODUCT_VERSION}`,
    dataCollection: {
      // IP · user-agent 추론(`infer_ip: never`)과 `user.*` 자동 채움을 끈다.
      userInfo: false,
      // 10.x 는 거부 목록이었지만 이 화면들은 인증을 쿠키로 하지 않아 잃을 것이 없으므로 아예 끈다.
      cookies: false,
      httpHeaders: { deny: PII_KEY_SNIPPETS },
      urlQueryParams: { deny: PII_KEY_SNIPPETS },
      httpBodies: [],
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
    },
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: TRACES_SAMPLE_RATES[ENVIRONMENT] ?? 0,
    tracePropagationTargets: [API_HOST],
    // 라우트 이동 시 apis.ts 가 진행 중인 요청을 일괄 취소하므로 그 취소 예외는 버린다.
    ignoreErrors: ["CanceledError", "AbortError", "canceled"],
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    // 스팬 스트리밍(SDK 11 기본)에서는 위 `beforeSendTransaction` 이 불리지 않으므로 스팬마다 같은 마스킹을 건다(30차 P30-01).
    beforeSendSpan: scrubSpan,
    /** 네비게이션·fetch breadcrumb 의 경로(`url`·`from`·`to`)에도 같은 마스킹을 건다. */
    beforeBreadcrumb(breadcrumb) {
      const data = breadcrumb.data;
      if (breadcrumb.category === "console" && Array.isArray(data?.arguments)) {
        data.arguments = data.arguments.map((arg: unknown) =>
          sanitizeLogArg(arg),
        );
      }
      if (data) {
        for (const key of ["url", "from", "to"]) {
          const value = maskIfString(data[key]);
          if (value !== undefined) {
            data[key] = value;
          }
        }
      }
      const message = maskIfString(breadcrumb.message);
      if (message !== undefined) {
        breadcrumb.message = message;
      }
      return breadcrumb;
    },
  });
}

/**
 * 결과를 확인하지 못한 쓰기(axios 시간 초과 · 게이트웨이 503/504)를 warning 으로 남긴다. `apis.ts` 가 기본 옵션의 GET 이
 * 아닌 호출을 토스트 뒤 실패 응답으로 끝내면(던지지 않음) 오류 이벤트가 생기지 않아, 게이트웨이 한도(30초) 초과 빈도를
 * 볼 수 없기 때문이다.
 *
 * 태그는 메서드와 **경로만** 싣는다. 쿼리 · 본문은 싣지 않고(개인정보가 섞일 수 있음), 경로의 비밀값은
 * `maskSecretPaths` 로 가리며, 숫자 id 는 `:id` 로 접어 같은 엔드포인트를 한 이슈로 모은다.
 */
export function reportUnconfirmedOutcome(
  method: string,
  url: string | undefined,
  kind: "timeout" | "gateway",
): void {
  const path = maskSecretPaths((url ?? "").split(/[?#]/)[0] ?? "").replace(
    /\/\d+(?=\/|$)/g,
    "/:id",
  );
  const upperMethod = method.toUpperCase();
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("http.method", upperMethod);
    scope.setTag("http.path", path);
    scope.setTag("unconfirmed.kind", kind);
    scope.setFingerprint(["unconfirmed-outcome", upperMethod, path]);
    Sentry.captureMessage(
      kind === "timeout"
        ? "API 응답 시간 초과(결과 확인 불가)"
        : "API 게이트웨이 503/504(결과 확인 불가)",
    );
  });
}

/** 이미 보낸 이상 징후 이름(세션 = 페이지 로드 한 번당 같은 이름은 1회만) */
const reportedAnomalies = new Set<string>();

/**
 * 화면이 「일어나면 안 되는 상태」(예: 서버가 준 값이 화면 계약과 어긋남)를 감지했을 때 warning 으로 남긴다. 던지는 오류가 아니라
 * 화면이 대체 동작으로 계속 가는 경우에 쓴다. 같은 `name` 은 세션당 1회만 보내 반복 렌더로 이벤트가 넘치지 않는다.
 *
 * `name` 은 고정 문자열(이슈 제목 · 묶음 기준)로 쓰고, 사용자 입력 · 이름 · 이메일 · id 를 넣지 않는다. `tags` 는 값이 몇 개로 정해진
 * 분류(문자열)만, `extra` 는 개수 같은 숫자만 싣는다 — 개인정보가 섞일 자리를 타입으로 막는다.
 */
export function reportClientAnomaly(
  name: string,
  tags?: Record<string, string>,
  extra?: Record<string, number>,
): void {
  if (reportedAnomalies.has(name)) return;
  reportedAnomalies.add(name);
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("client.anomaly", name);
    for (const [key, value] of Object.entries(tags ?? {})) {
      scope.setTag(key, value);
    }
    if (extra) scope.setExtras(extra);
    scope.setFingerprint(["client-anomaly", name]);
    Sentry.captureMessage(`화면 이상 징후: ${name}`);
  });
}

/**
 * 이슈를 사용자·워크스페이스 단위로 묶기 위한 컨텍스트.
 * - 이름·이메일 같은 식별 정보는 싣지 않고 내부 id 와 로그인 id 만 남긴다.
 */
export function setSentryUser(info: TokenClaims): void {
  Sentry.setUser({ id: String(info.id), username: info.loginId });
}

export function setSentryWorkspace(workspace: Workspace): void {
  Sentry.setTag("workspace.id", workspace.id || null);
  Sentry.setTag("workspace.name", workspace.name || null);
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
  Sentry.setTag("workspace.id", null);
  Sentry.setTag("workspace.name", null);
}
